import { useForm } from 'react-hook-form'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { queryClient } from '@/modules/shared/lib/query-client'
import { QUERY_KEYS } from '@/modules/shared/constants/query-keys.contant'
import { useOrdersSearchStore } from '../../stores/order-search.store'

const isObjectEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true
  if (!obj1 || !obj2 || typeof obj1 !== 'object' || typeof obj2 !== 'object') return false

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) return false

  return keys1.every(key => {
    if (!Object.prototype.hasOwnProperty.call(obj2, key)) return false
    return isObjectEqual(obj1[key], obj2[key])
  })
}

export type FilterFormValues = {
  codigo?: string
  dataInicio?: string
  dataFim?: string
  cliente?: string
  cnpjcpf?: string
  status?: string
}

export type FilterTypes = 'codigo' | 'periodo'

export const useFilterOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filterType, setFilterType] = useState<FilterTypes>('periodo')
  const [showTodayOrders, setShowTodayOrders] = useState(() => {
    return searchParams.get('today') === 'true'
  })

  const lastAppliedFiltersRef = useRef<FilterFormValues>({})

  const {
    filters,
    actions: { setFilters, setOrders },
  } = useOrdersSearchStore()

  const form = useForm<FilterFormValues>({
    defaultValues: {
      codigo: '',
      dataInicio: '',
      dataFim: '',
      cliente: '',
      cnpjcpf: '',
      status: '',
    },
  })

  const filtersHaveChanged = useCallback((newFilters: FilterFormValues): boolean => {
    const cleanNewFilters = Object.entries(newFilters)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .reduce((acc, [key, value]) => {
        acc[key] = value
        return acc
      }, {} as Record<string, any>)

    const cleanLastFilters = Object.entries(lastAppliedFiltersRef.current)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .reduce((acc, [key, value]) => {
        acc[key] = value
        return acc
      }, {})

    return !isObjectEqual(cleanNewFilters, cleanLastFilters)
  }, [])

  const updateQueryParams = useCallback((filters: FilterFormValues, showToday = false) => {
    const newParams = new URLSearchParams()

    if (showToday) {
      newParams.set('today', 'true')
    }

    for (const [key, value] of Object.entries(filters)) {
      if (value && value.trim() !== '') {
        newParams.set(key, value)
      }
    }

    if (filtersHaveChanged(filters)) {
      setSearchParams(newParams, { replace: true })
      lastAppliedFiltersRef.current = { ...filters }
      return true
    }

    return false
  }, [setSearchParams, filtersHaveChanged])

  const handleToggleTodayOrders = useCallback((show: boolean) => {
    if (show === showTodayOrders) {
      return
    }

    setShowTodayOrders(show)

    form.reset({
      codigo: '',
      dataInicio: '',
      dataFim: '',
      cliente: '',
      cnpjcpf: '',
      status: '',
    })

    const newParams = new URLSearchParams()
    if (show) {
      newParams.set('today', 'true')
    }
    setSearchParams(newParams, { replace: true })

    setFilters({})
    queryClient.removeQueries([QUERY_KEYS.ORDERS])
    lastAppliedFiltersRef.current = {}
  }, [showTodayOrders, form, setSearchParams, setFilters])

  const handleFormSubmit = useCallback((data: FilterFormValues) => {
    if (!filtersHaveChanged(data)) {
      return
    }

    if (showTodayOrders) {
      setShowTodayOrders(false)
    }

    updateQueryParams(data, false)
    setFilters(data)
    queryClient.removeQueries([QUERY_KEYS.ORDERS])
  }, [filtersHaveChanged, showTodayOrders, updateQueryParams, setFilters])

  const handleRemoveFilter = useCallback((filterType: string) => {
    let updatedFilters = { ...filters }

    if (filterType === 'data') {
      updatedFilters = {
        ...updatedFilters,
        dataInicio: '',
        dataFim: '',
      }
      form.setValue('dataInicio', '')
      form.setValue('dataFim', '')
    } else if (filterType === 'cliente') {
      updatedFilters = {
        ...updatedFilters,
        cliente: '',
      }
      form.setValue('cliente', '')
    } else if (filterType === 'codigo') {
      updatedFilters = {
        ...updatedFilters,
        codigo: '',
      }
      form.setValue('codigo', '')
    } else if (filterType === 'status') {
      updatedFilters = {
        ...updatedFilters,
        status: '',
      }
      form.setValue('status', '')
    } else if (filterType === 'cnpjcpf') {
      updatedFilters = {
        ...updatedFilters,
        cnpjcpf: '',
      }
      form.setValue('cnpjcpf', '')
    }

    if (!filtersHaveChanged(updatedFilters)) {
      return
    }

    updateQueryParams(updatedFilters, false)
    setFilters(updatedFilters)
    queryClient.removeQueries([QUERY_KEYS.ORDERS])
  }, [filters, form, filtersHaveChanged, updateQueryParams, setFilters])

  const handleChangeFilterType = useCallback((type: FilterTypes) => {
    setFilterType(type)
  }, [])

  const handleCancel = useCallback(() => {
    const hasActiveFilters = Object.values(filters).some(
      filter => filter !== undefined && filter !== null && filter !== ''
    )

    if (!hasActiveFilters) {
      form.reset()
      return
    }

    form.reset({
      codigo: '',
      dataInicio: '',
      dataFim: '',
      cliente: '',
      cnpjcpf: '',
      status: '',
    })

    const newParams = new URLSearchParams()
    setSearchParams(newParams, { replace: true })
    setFilters({})
    lastAppliedFiltersRef.current = {}
    queryClient.removeQueries([QUERY_KEYS.ORDERS])
  }, [filters, form, setSearchParams, setFilters])

  useEffect(() => {
    const codigo = searchParams.get('codigo') || ''
    const dataInicio = searchParams.get('dataInicio') || ''
    const dataFim = searchParams.get('dataFim') || ''
    const cliente = searchParams.get('cliente') || ''
    const cnpjcpf = searchParams.get('cnpjcpf') || ''
    const status = searchParams.get('status') || ''
    const today = searchParams.get('today') === 'true'

    if (codigo) {
      setFilterType('codigo')
    }

    form.reset({
      codigo,
      dataInicio,
      dataFim,
      cliente,
      cnpjcpf,
      status,
    })

    setFilters({
      codigo,
      dataInicio,
      dataFim,
      cliente,
      cnpjcpf,
      status,
    })

    setShowTodayOrders(today)

    lastAppliedFiltersRef.current = {
      codigo,
      dataInicio,
      dataFim,
      cliente,
      cnpjcpf,
      status,
    }
  }, [])

  return {
    form,
    filters,
    filterType,
    showTodayOrders,
    handleFormSubmit,
    handleChangeFilterType,
    handleRemoveFilter,
    handleCancel,
    setShowTodayOrders: handleToggleTodayOrders,
  }
}